import { useCallback, useEffect, useState, type ComponentType } from "react";
import {
  ArrowRight,
  Banknote,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  GraduationCap,
  HeartPulse,
  Landmark,
  Scale,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import Seo from "../components/Seo";
import { buildServiceSchema } from "../utils/structuredData";

type Workflow = { label: string; detail: string };

type Industry = {
  slug: string;
  title: string;
  // Short label for the toggle. Kept distinct from `title` so the full name
  // appears exactly once on the page (inside the active panel heading).
  tab: string;
  onePagerPath: string;
  icon: ComponentType<{ className?: string }>;
  description: string;
  challenge: string;
  outcomes: string[];
  workflows: Workflow[];
  // Real, attributable client result — only set this when the claim is
  // verifiably true for this specific industry. Do not reuse across verticals.
  proofPoint?: string;
};

const industryExamples: Industry[] = [
  {
    slug: "government",
    title: "Municipal Government",
    tab: "Government",
    onePagerPath: "/assets/1-pagers/Government 1-Pager.pdf",
    icon: Landmark,
    description:
      "Modernize resident services and internal operations without disrupting the systems, approvals, and public accountability your agency already relies on.",
    challenge:
      "Records requests, permits, and recurring reports pile up when staff move information by hand across disconnected departments and systems.",
    outcomes: ["Shorter response times", "Consistent public-records handling", "More time for constituent work"],
    proofPoint: "Our automated reporting and analytics helped Newark, NJ's Department of Public Works avoid an estimated $3–4M in costs.",
    workflows: [
      { label: "Records-request search", detail: "Find and assemble responsive documents across departments in minutes." },
      { label: "Permit routing", detail: "Send applications to the right reviewers with status tracked end to end." },
      { label: "Compliance reporting", detail: "Assemble recurring state and federal reports from source systems." },
      { label: "Constituent intake", detail: "Turn emails, forms, and calls into structured, routed requests." },
      { label: "Agenda & packet prep", detail: "Compile meeting packets and minutes from prior records." },
      { label: "Policy & code lookup", detail: "Answer staff questions from municipal code and internal policy instantly." },
    ],
  },
  {
    slug: "financial-services",
    title: "Financial Services",
    tab: "Financial Services",
    onePagerPath: "/assets/1-pagers/Financial Services 1-Pager.pdf",
    icon: Banknote,
    description:
      "Deploy a private automation layer for firms and banks that need faster operations without compromising sensitive client data or regulatory obligations.",
    challenge:
      "Scattered client records, disconnected legacy systems, and manual processes consume advisor time while SEC, FINRA, and GLBA obligations limit the use of public AI tools.",
    outcomes: ["Faster client-record retrieval", "Less manual processing", "Auditable, governed workflows"],
    workflows: [
      { label: "Client-record search", detail: "Retrieve trusted information across CRMs, portfolio systems, and legacy tools." },
      { label: "Onboarding & KYC/AML", detail: "Route onboarding steps and checks through a structured, repeatable workflow." },
      { label: "Dynamic SOPs", detail: "Turn internal documentation into living playbooks for advisors and operations teams." },
      { label: "Systems integration", detail: "Connect existing infrastructure with APIs, connectors, and webhooks." },
      { label: "Compliance reporting", detail: "Create structured, auditable records for SEC and FINRA examinations." },
      { label: "Secure archiving", detail: "Organize cloud storage, document management, databases, and archives." },
    ],
  },
  {
    slug: "distribution",
    title: "Distribution & Wholesale",
    tab: "Distribution",
    onePagerPath: "/assets/1-pagers/Distributor 1-Pager.pdf",
    icon: Building2,
    description:
      "Keep procurement, logistics, and compliance moving across sites, suppliers, and systems that were never designed to talk to each other.",
    challenge:
      "Supplier records, licenses, and shipment documents live in inboxes and spreadsheets scattered across every location.",
    outcomes: ["Fewer manual handoffs", "Fewer compliance gaps", "Consistency across sites"],
    workflows: [
      { label: "Supplier onboarding", detail: "Collect, validate, and file supplier documents automatically." },
      { label: "License renewals", detail: "Track expirations and trigger renewals before they lapse." },
      { label: "Shipment document checks", detail: "Match POs, invoices, and BOLs and flag discrepancies." },
      { label: "Order & invoice matching", detail: "Reconcile three-way matches across ERP and email." },
      { label: "Inventory alerts", detail: "Surface stock and reorder signals across warehouses." },
      { label: "Compliance recordkeeping", detail: "Keep audit-ready records for regulated products." },
    ],
  },
  {
    slug: "education",
    title: "Education & Research",
    tab: "Education",
    onePagerPath: "/assets/1-pagers/Education 1-Pager.pdf",
    icon: GraduationCap,
    description:
      "Automate high-volume administrative work while protecting confidential student, staff, and research records.",
    challenge:
      "Lean teams spend hours on enrollment, records, and policy questions that repeat every single term.",
    outcomes: ["Less repetitive paperwork", "Protected confidential records", "Faster answers for staff"],
    workflows: [
      { label: "Enrollment processing", detail: "Validate and route applications and forms end to end." },
      { label: "Policy search", detail: "Answer staff and student questions from handbooks and policy." },
      { label: "Records workflows", detail: "Digitize, organize, and retrieve institutional records securely." },
      { label: "Grant & research admin", detail: "Assemble reporting and track compliance requirements." },
      { label: "Document requests", detail: "Fulfill routine transcript and record requests without manual lookups." },
      { label: "Helpdesk triage", detail: "Deflect common IT and admin questions with grounded answers." },
    ],
  },
  {
    slug: "healthcare",
    title: "Healthcare & Biotech",
    tab: "Healthcare",
    onePagerPath: "/assets/1-pagers/Healthcare 1-Pager.pdf",
    icon: HeartPulse,
    description:
      "Make regulated records easier to retrieve and administrative work easier to manage, without moving data outside your control.",
    challenge:
      "Clinical and operational documentation is spread across systems, making retrieval and audit prep slow.",
    outcomes: ["Audit-ready records", "Less administrative burden", "Data stays in your environment"],
    workflows: [
      { label: "Audit preparation", detail: "Assemble evidence and documentation for audits automatically." },
      { label: "Intake routing", detail: "Turn intake forms and referrals into structured, routed records." },
      { label: "SOP knowledge search", detail: "Answer staff questions from current SOPs and policy." },
      { label: "Records retrieval", detail: "Find documentation across systems in seconds." },
      { label: "Form pre-fill", detail: "Draft and pre-fill routine administrative and authorization forms." },
      { label: "Compliance monitoring", detail: "Track requirements and flag gaps before review." },
    ],
  },
  {
    slug: "law",
    title: "Law & Legal Services",
    tab: "Legal",
    onePagerPath: "/assets/1-pagers/Legal 1-Pager.pdf",
    icon: Scale,
    description:
      "Accelerate high-trust document and matter work while keeping privilege and client confidentiality fully intact.",
    challenge:
      "Intake, retrieval, and matter admin consume billable time that is better spent on client work.",
    outcomes: ["More time for billable work", "Privilege kept intact", "Faster document retrieval"],
    workflows: [
      { label: "Client intake", detail: "Structure new-matter intake and run conflict checks." },
      { label: "Precedent retrieval", detail: "Find relevant prior work and clauses across your files." },
      { label: "Matter workflows", detail: "Automate routine steps across the matter lifecycle." },
      { label: "Document review support", detail: "Summarize and organize large document sets." },
      { label: "Deadline & docket tracking", detail: "Surface dates and obligations automatically." },
      { label: "Knowledge search", detail: "Answer questions from your firm's own documents." },
    ],
  },
];

const pad = (n: number) => String(n).padStart(2, "0");

function IndustrySlide({ industry, index, total }: { industry: Industry; index: number; total: number }) {
  const Icon = industry.icon;
  return (
    <div className="grid items-stretch lg:grid-cols-5 h-full">
      {/* Left: brand identity + outcomes + CTA */}
      <div className="relative flex min-h-[420px] flex-col justify-between overflow-hidden bg-primary p-8 sm:p-10 lg:col-span-2 lg:p-12">
        <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-25" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />

        <div className="relative flex items-start justify-between">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm">
            <Icon className="h-8 w-8" aria-hidden="true" />
          </div>
          <span className="font-sans text-sm font-medium tracking-[0.2em] text-white/70">
            {pad(index + 1)} / {pad(total)}
          </span>
        </div>

        <div className="relative my-8">
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight text-white mb-4">{industry.title}</h2>
          <p className="text-base sm:text-lg leading-relaxed text-white/85">{industry.description}</p>
        </div>

        <div className="relative">
          <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-white/60 mb-3">What changes</p>
          <ul className="space-y-2 mb-8">
            {industry.outcomes.map((outcome) => (
              <li key={outcome} className="flex items-center gap-2.5 text-white/90">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-white" aria-hidden="true" />
                <span className="text-sm sm:text-base">{outcome}</span>
              </li>
            ))}
          </ul>
          {industry.proofPoint && (
            <p className="mb-6 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm leading-relaxed text-white/95 backdrop-blur-sm">
              {industry.proofPoint}
            </p>
          )}
          <a
            href={encodeURI(industry.onePagerPath)}
            download={`Contextral-${industry.slug}-1-pager.pdf`}
            className="group inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-surface px-6 py-3.5 text-base font-semibold text-primary shadow-lg transition-transform duration-200 hover:scale-[1.02] sm:w-auto"
          >
            <Download className="h-4 w-4 flex-shrink-0 transition-transform group-hover:-translate-y-0.5" aria-hidden="true" />
            Download {industry.tab} 1-pager
          </a>
        </div>
      </div>

      {/* Right: the challenge + concrete workflows */}
      <div className="flex flex-col bg-surface p-8 sm:p-10 lg:col-span-3 lg:p-12">
        <div className="mb-6">
          <p className="eyebrow mb-2">The challenge</p>
          <p className="text-base sm:text-lg text-text-secondary leading-relaxed">{industry.challenge}</p>
        </div>

        <div className="h-px w-full bg-borderSubtle mb-6" aria-hidden="true" />

        <p className="eyebrow mb-4">Workflows we automate</p>
        <div className="grid flex-1 gap-3 sm:grid-cols-2">
          {industry.workflows.map((workflow) => (
            <div
              key={workflow.label}
              className="rounded-2xl border border-borderSubtle bg-surfaceSoft p-4 transition-all duration-200 hover:border-primary/40 hover:shadow-md hover:shadow-primary/5"
            >
              <div className="mb-1.5 flex items-center gap-2.5">
                <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-accentPurple text-white">
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <h3 className="text-base font-semibold text-text-primary mb-0">{workflow.label}</h3>
              </div>
              <p className="text-sm text-text-secondary leading-relaxed">{workflow.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function IndustryExamples() {
  const location = useLocation();
  const [active, setActive] = useState(0);
  const total = industryExamples.length;

  const go = useCallback((i: number) => setActive(((i % total) + total) % total), [total]);

  // Left / right arrows cycle industries when the showcase is on screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") go(active + 1);
      else if (event.key === "ArrowLeft") go(active - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, go]);

  const seoDescription =
    "Explore secure AI automation examples for municipal government, financial services, distribution, education, healthcare, and legal services.";
  const serviceSchema = buildServiceSchema(location.pathname, "AI Automation Industry Examples", seoDescription);

  return (
    <div className="relative pt-6">
      <Seo
        title="AI Automation Industry Examples | Contextral"
        description={seoDescription}
        jsonLd={serviceSchema}
      />

      <section>
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Industry Examples", path: "/industry-examples" }]} />
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            See Secure Automation in Context
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            The technology changes by environment. The goal stays the same: less manual work, faster access to trusted
            information, and workflows your team can actually use.
          </p>
        </div>

        {/* Industry toggle */}
        <div role="tablist" aria-label="Industries" className="flex flex-wrap justify-center gap-2 sm:gap-2.5 mb-6 sm:mb-8">
          {industryExamples.map((industry, i) => {
            const isActive = i === active;
            const TabIcon = industry.icon;
            return (
              <button
                key={industry.slug}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => go(i)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-primary text-white shadow-md shadow-primary/25"
                    : "border border-borderSubtle bg-surface text-text-secondary hover:border-primary/40 hover:text-primary"
                }`}
              >
                <TabIcon className="h-4 w-4" aria-hidden="true" />
                {industry.tab}
              </button>
            );
          })}
        </div>

        {/* Sliding showcase — every industry stays mounted; the track translates. */}
        <div className="relative overflow-hidden rounded-3xl border border-borderSubtle bg-surface shadow-xl shadow-primary/10">
          <div
            className="flex w-full transition-transform duration-500 ease-out motion-reduce:transition-none"
            style={{ transform: `translateX(-${active * 100}%)` }}
          >
            {industryExamples.map((industry, i) => (
              <div key={industry.slug} className="w-full flex-shrink-0" aria-roledescription="slide">
                <IndustrySlide industry={industry} index={i} total={total} />
              </div>
            ))}
          </div>
        </div>

        {/* Progress controls */}
        <div className="mt-6 flex items-center justify-center gap-5">
          <button
            type="button"
            onClick={() => go(active - 1)}
            aria-label="Previous industry"
            className="inline-flex items-center justify-center rounded-full border border-borderSubtle bg-surface p-2 text-text-secondary transition-colors hover:border-primary/40 hover:text-primary"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2" role="presentation">
            {industryExamples.map((industry, i) => (
              <button
                key={industry.slug}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show ${industry.tab}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === active ? "w-7 bg-primary" : "w-2 bg-borderSubtle hover:bg-primary/40"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => go(active + 1)}
            aria-label="Next industry"
            className="inline-flex items-center justify-center rounded-full border border-borderSubtle bg-surface p-2 text-text-secondary transition-colors hover:border-primary/40 hover:text-primary"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
}
